import apiClient from './apiClient';
import { normalizeAdminBackups } from '../components/evm/vmdetail/utils';

/**
 * 独享容器 (Kata) API — 用户: /api/evm/user/containers/*，后台: /api/evm/admin/containers/*。
 * 统一返回 { status, msg, data }；网络/HTTP 错误归一成 { status: 'error', msg }。
 */
const wrap = async (p) => {
    try {
        const r = await p;
        return r.data;
    } catch (e) {
        const d = e.response?.data || {};
        return { status: 'error', msg: d.msg || d.error || d.message || e.message };
    }
};

export const containerUserApi = {
    list: () => wrap(apiClient.get('/api/evm/user/containers')),
    catalog: () => wrap(apiClient.get('/api/evm/user/containers/catalog')),
    order: (data) => wrap(apiClient.post('/api/evm/user/containers/order', data)),
    get: (id) => wrap(apiClient.get(`/api/evm/user/containers/${id}`)),
    power: (id, action) => wrap(apiClient.post(`/api/evm/user/containers/${id}/power`, { action })),
    remove: (id) => wrap(apiClient.post(`/api/evm/user/containers/${id}/delete`)),
    logs: (id) => wrap(apiClient.get(`/api/evm/user/containers/${id}/logs`)),
    addPort: (id, data) => wrap(apiClient.post(`/api/evm/user/containers/${id}/ports`, data)),
    deletePort: (id, pid) => wrap(apiClient.post(`/api/evm/user/containers/${id}/ports/${pid}/delete`)),
    setSecurityGroups: (id, ids) => wrap(apiClient.post(`/api/evm/user/containers/${id}/security-groups`, { ids })),
    domains: (id) => wrap(apiClient.get(`/api/evm/user/containers/${id}/domains`)),
    securityGroupList: () => wrap(apiClient.get('/api/user/evm/security-group')),
    templates: () => wrap(apiClient.get('/api/evm/user/containers/templates')),
    terminal: (id) => wrap(apiClient.get(`/api/evm/user/containers/${id}/terminal`)),
    files: (id) => wrap(apiClient.get(`/api/evm/user/containers/${id}/files`)),
    config: (id, data) => wrap(apiClient.post(`/api/evm/user/containers/${id}/config`, data)),
    addons: (id, gb) => wrap(apiClient.get(`/api/evm/user/containers/${id}/addons`, { params: gb ? { gb } : {} })),
    buyDisk: (id, gb) => wrap(apiClient.post(`/api/evm/user/containers/${id}/disk`, { gb })),
    buyEip: (id) => wrap(apiClient.post(`/api/evm/user/containers/${id}/eip`)),
    releaseEip: (id, eid) => wrap(apiClient.post(`/api/evm/user/containers/${id}/eip/${eid}/release`)),
    changePlan: (id, productId) => wrap(apiClient.post(`/api/evm/user/containers/${id}/plan`, { product_id: productId })),
    /** 计划任务 + 后台服务（整体替换） */
    apps: (id, data) => wrap(apiClient.post(`/api/evm/user/containers/${id}/apps`, data)),
    runCron: (id, job) => wrap(apiClient.post(`/api/evm/user/containers/${id}/cron/${encodeURIComponent(job)}/run`)),
    php: (id, settings) => wrap(apiClient.post(`/api/evm/user/containers/${id}/php`, { settings })),
    /** 出口：off | v4 | v6 | dual（Cloudflare WARP） */
    egress: (id, data) => wrap(apiClient.post(`/api/evm/user/containers/${id}/egress`, data)),
};

export const containerAdminApi = {
    list: (params) => wrap(apiClient.get('/api/evm/admin/containers', { params })),
    get: (id) => wrap(apiClient.get(`/api/evm/admin/containers/${id}`)),
    power: (id, action) => wrap(apiClient.post(`/api/evm/admin/containers/${id}/power`, { action })),
    remove: (id) => wrap(apiClient.post(`/api/evm/admin/containers/${id}/delete`)),
    logs: (id) => wrap(apiClient.get(`/api/evm/admin/containers/${id}/logs`)),
    nodes: () => wrap(apiClient.get('/api/evm/admin/containers/nodes')),
    updateNode: (id, data) => wrap(apiClient.post(`/api/evm/admin/containers/nodes/${id}`, data)),
    plans: () => wrap(apiClient.get('/api/evm/admin/containers/plans')),
    terminal: (id) => wrap(apiClient.get(`/api/evm/admin/containers/${id}/terminal`)),
    files: (id) => wrap(apiClient.get(`/api/evm/admin/containers/${id}/files`)),
    /** 不带 port_limit 时只返回当前值 */
    settings: (data = {}) => wrap(apiClient.post('/api/evm/admin/containers/settings', data)),
    /** 已付款待开通（资源不足排队）：订单 + 待生效的升级/附加项 */
    pending: (status = 'pending') => wrap(apiClient.get('/api/evm/admin/containers/pending', { params: { status } })),
    retryPending: (subId, nodeId) => wrap(apiClient.post(`/api/evm/admin/containers/pending/${subId}/retry`, nodeId ? { node_id: nodeId } : {})),
    cancelPending: (subId) => wrap(apiClient.post(`/api/evm/admin/containers/pending/${subId}/cancel`)),
    updatePlan: (id, data) => wrap(apiClient.post(`/api/evm/admin/containers/plans/${id}`, data)),
    templates: () => wrap(apiClient.get('/api/evm/admin/containers/templates')),
    legacy: (id) => wrap(apiClient.get(`/api/evm/admin/containers/legacy/${id}`)),
    migrateLegacy: (id, nodeId) => wrap(apiClient.post(`/api/evm/admin/containers/legacy/${id}/migrate`, nodeId ? { node_id: nodeId } : {})),
    legacyMigration: (id) => wrap(apiClient.get(`/api/evm/admin/containers/legacy/${id}/migration`)),
    cancelPendingAddon: (itemId) => wrap(apiClient.post(`/api/evm/admin/pending-provisions/elastic/${itemId}/cancel`)),
    egress: (id, data) => wrap(apiClient.post(`/api/evm/admin/containers/${id}/egress`, data)),
    /** Cloudflare WARP：节点状态 + WARP+ 授权 */
    warp: () => wrap(apiClient.get('/api/evm/admin/containers/warp')),
    warpNode: (id, data) => wrap(apiClient.post(`/api/evm/admin/containers/warp/nodes/${id}`, data)),
    warpAddLicense: (data) => wrap(apiClient.post('/api/evm/admin/containers/warp/licenses', data)),
    warpUpdateLicense: (id, data) => wrap(apiClient.post(`/api/evm/admin/containers/warp/licenses/${id}`, data)),
    warpDeleteLicense: (id) => wrap(apiClient.post(`/api/evm/admin/containers/warp/licenses/${id}/delete`)),
};

/**
 * 容器定时加密备份：同时充当 AutoBackupCard 的 api 与 adapter（resType="container"）。
 * 用户: /api/evm/user/containers/{id}/backup-*；后台: /api/evm/admin/backups/*?type=container。
 */
export const containerBackupApi = (mode) => {
    if (mode === 'admin') {
        const b = '/api/evm/admin/backups';
        return {
            isAdmin: true,
            getBackups: async (id) => normalizeAdminBackups(await wrap(apiClient.get(`${b}/vms/${id}`, { params: { type: 'container' } }))),
            runBackupNow: (id) => wrap(apiClient.post(`${b}/vms/${id}/run`, { type: 'container' })),
            restoreBackupPoint: (id, pid, body = {}) => wrap(apiClient.post(`${b}/points/${pid}/restore`, { mode: 'same', power_on: body.power_on || 'keep' })),
            deleteBackupPoint: (id, pid) => wrap(apiClient.post(`${b}/points/${pid}/delete`)),
            verifyBackupPoint: (pid) => wrap(apiClient.post(`${b}/points/${pid}/verify`)),
        };
    }
    const b = '/api/evm/user/containers';
    return {
        isAdmin: false,
        getBackups: async (id) => {
            const [p, pts] = await Promise.all([wrap(apiClient.get(`${b}/${id}/backup-plan`)), wrap(apiClient.get(`${b}/${id}/backup-points`))]);
            if (!isOk(p)) return p;
            const d = isOk(pts) ? (pts.data || {}) : {};
            const items = Array.isArray(d) ? d : (Array.isArray(d.items) ? d.items : []);
            return { status: 'success', data: { plan: p.data || {}, items, jobs: Array.isArray(d.jobs) ? d.jobs : [] } };
        },
        runBackupNow: (id) => wrap(apiClient.post(`${b}/${id}/backup-points/run`, {})),
        restoreBackupPoint: (id, pid, body = {}) => wrap(apiClient.post(`${b}/${id}/backup-points/${pid}/restore`, { power_on: body.power_on || 'keep' })),
        deleteBackupPoint: (id, pid) => wrap(apiClient.post(`${b}/${id}/backup-points/${pid}/delete`, {})),
    };
};

export const isOk = (r) => r && r.status === 'success';
export const errMsg = (r, fallback) => (r && (r.msg || r.error)) || fallback;
